/**
 * `AwaitingUserBanner` — load-bearing "agents need your input" chip
 * at the top of the Team of Agents pane (plan Part D + §B.1 goal #2).
 *
 * Visible whenever `awaitingUserCount > 0`. Cannot be permanently
 * dismissed; the next poll re-evaluates the count and re-renders.
 * Per-session dismissal could be added in v1.1; v1 keeps it
 * persistent because the "must see it on return" requirement is
 * the whole point of this surface.
 *
 * Singular vs plural copy via i18n's count interpolation
 * (en/pt/es plurals).
 */
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface AwaitingUserBannerProps {
  count: number;
}

export function AwaitingUserBanner({ count }: AwaitingUserBannerProps) {
  const { t } = useTranslation();
  if (count <= 0) return null;
  return (
    <div
      data-testid="awaiting-user-banner"
      role="status"
      aria-live="polite"
      className="flex items-start gap-2 border-b border-amber-200/60 bg-amber-50 px-3 py-2 text-xs dark:border-amber-900/40 dark:bg-amber-950/30"
    >
      <AlertTriangle
        size={14}
        className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400"
        aria-hidden
      />
      <p className="flex-1 text-amber-900 dark:text-amber-200">
        {t("rightSidebar.awaitingBanner", { count })}
      </p>
    </div>
  );
}
